import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosInstance from '../../utils/axiosConfig';

const TrainingPathManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paths, setPaths] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingPath, setEditingPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    priceDisplay: '',
    features: ['']
  });

  useEffect(() => {
    loadPaths();
  }, []);

  const loadPaths = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axiosInstance.get('/api/v2/training-paths/admin');
      
      if (!response.data) {
        console.warn('No training paths data received');
        setPaths([]);
        return;
      }

      if (Array.isArray(response.data)) {
        setPaths(response.data);
      } else {
        console.error('Invalid training paths data format:', response.data);
        setError('Failed to load training paths: Invalid data format');
        setPaths([]);
      }
    } catch (error) {
      console.error('Error loading training paths:', error);
      setError(error.response?.data?.message || 'Failed to load training paths. Please try again.');
      setPaths([]);
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (path = null) => {
    if (path) {
      setEditingPath(path);
      setFormData({
        title: path.title,
        price: path.price,
        priceDisplay: path.priceDisplay,
        features: path.features
      });
    } else {
      setEditingPath(null);
      setFormData({
        title: '',
        price: '',
        priceDisplay: '',
        features: ['']
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPath(null);
    setFormData({
      title: '',
      price: '',
      priceDisplay: '',
      features: ['']
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        features: formData.features.filter(f => f.trim())
      };

      if (editingPath) {
        await axiosInstance.put(`/api/v2/training-paths/${editingPath.id}`, payload);
      } else {
        await axiosInstance.post('/api/v2/training-paths', payload);
      }

      setSuccessMessage(editingPath ? 'Training path updated successfully!' : 'Training path created successfully!');
      handleClose();
      loadPaths();
    } catch (error) {
      console.error('Error saving training path:', error);
      setError(error.response?.data?.message || 'Failed to save training path. Please try again.');
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training path?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/v2/training-paths/${id}`);
      setSuccessMessage('Training path deleted successfully!');
      loadPaths();
    } catch (error) {
      console.error('Error deleting training path:', error);
      setError(error.response?.data?.message || 'Failed to delete training path');
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedPaths = Array.from(paths);
    const [removed] = reorderedPaths.splice(result.source.index, 1);
    reorderedPaths.splice(result.destination.index, 0, removed);

    // Update the order property of each path
    const updatedPaths = reorderedPaths.map((path, index) => ({
      ...path,
      order: index
    }));

    setPaths(updatedPaths);

    try {
      await axiosInstance.put('/api/v2/training-paths/reorder-paths', {
        paths: updatedPaths.map((path, index) => ({
          id: path.id,
          order: index
        }))
      });
    } catch (error) {
      console.error('Error reordering paths:', error);
      setError('Failed to save new order');
      loadPaths(); // Reload original order
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Training Paths</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add New Path
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="paths">
          {(provided) => (
            <TableContainer component={Paper} {...provided.droppableProps} ref={provided.innerRef}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell width={120}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paths.map((path, index) => (
                    <Draggable key={path.id} draggableId={path.id} index={index}>
                      {(provided) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <TableCell {...provided.dragHandleProps}>
                            <DragIndicatorIcon />
                          </TableCell>
                          <TableCell>{path.title}</TableCell>
                          <TableCell>{path.priceDisplay}</TableCell>
                          <TableCell>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {path.features.map((feature, i) => (
                                <li key={i}>{feature}</li>
                              ))}
                            </ul>
                          </TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleOpen(path)} size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(path.id)} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPath ? 'Edit Training Path' : 'New Training Path'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Price Display (e.g., '$49/month')"
              name="priceDisplay"
              value={formData.priceDisplay}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Features</Typography>
            {formData.features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label={`Feature ${index + 1}`}
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  required
                />
                <IconButton 
                  onClick={() => removeFeature(index)}
                  disabled={formData.features.length <= 1}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button onClick={addFeature} variant="outlined" sx={{ mb: 2 }}>
              Add Feature
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingPath ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingPathManagement;
